import { getGuideTourTargetAvailability } from '../hooks'
import {
  getGuideTourStepRoute,
  isGuideTourStepOnPathname,
  isMultiRouteGuideTour,
} from '../routing/utils'
import type { GuideTourDefinition, GuideTourStep } from '../types'
import { isGuideTourStepExpired } from './tourRuntime'

export const hasGuideTourStepTarget = (
  step: GuideTourStep | null | undefined
): step is GuideTourStep & { targetId: string } =>
  Boolean(step?.targetId?.trim())

export const isGuideTourStepTargetInteractionBlocked = (
  step: GuideTourStep | null | undefined
): boolean =>
  hasGuideTourStepTarget(step) && step.allowTargetInteraction !== true

export const getGuideTourStepForceClickTargetId = (
  step: GuideTourStep | null | undefined
): string | null => {
  const targetId = step?.forceClick?.trim()

  return targetId ? targetId : null
}

export const getGuideTourStepForceClickHighlightTargetId = (
  step: GuideTourStep | null | undefined
): string | null => {
  const targetId = step?.targetIdForceClicked?.trim()

  return targetId ? targetId : null
}

/** Ripples + hand only when `targetIdForceClicked` is set (`forceClick` alone does not show affordance). */
export const getGuideTourStepForceClickAffordanceTargetId = (
  step: GuideTourStep | null | undefined
): string | null => getGuideTourStepForceClickHighlightTargetId(step)

export const shouldShowGuideTourForceClickAffordance = (
  step: GuideTourStep | null | undefined
): boolean => getGuideTourStepForceClickAffordanceTargetId(step) !== null

export const shouldShowGuideTourForceClickHighlight = (
  step: GuideTourStep | null | undefined
): boolean => getGuideTourStepForceClickHighlightTargetId(step) !== null

export const isGuideTourForceClickStep = (
  step: GuideTourStep | null | undefined
): boolean => getGuideTourStepForceClickTargetId(step) !== null

type GuideTourRouteScrollState = {
  originY: number
  forced: boolean
}

const routeScrollStateByPath = new Map<string, GuideTourRouteScrollState>()

const getCurrentGuideTourPathname = (): string =>
  typeof window === 'undefined' ? '/' : window.location.pathname

/** Records the page scroll position before the tour forces a programmatic scroll. */
export const prepareGuideTourForcedScroll = (
  pathname: string = getCurrentGuideTourPathname()
): void => {
  const existing = routeScrollStateByPath.get(pathname)

  if (!existing) {
    routeScrollStateByPath.set(pathname, {
      originY: window.scrollY,
      forced: true,
    })
    return
  }

  if (!existing.forced) {
    routeScrollStateByPath.set(pathname, {
      originY: existing.originY,
      forced: true,
    })
  }
}

export const wasGuideTourScrollForcedOnPath = (pathname: string): boolean =>
  routeScrollStateByPath.get(pathname)?.forced ?? false

export const getGuideTourScrollRestoreTarget = (
  pathname: string = getCurrentGuideTourPathname()
): number | undefined => {
  const state = routeScrollStateByPath.get(pathname)

  if (!state?.forced) {
    return undefined
  }

  return state.originY
}

export const clearGuideTourRouteScrollState = (
  pathname: string = getCurrentGuideTourPathname()
): void => {
  routeScrollStateByPath.delete(pathname)
}

export const resetGuideTourRouteScrollStateForTests = (): void => {
  routeScrollStateByPath.clear()
}

export {
  forceClickGuideTourElement,
  forceClickGuideTourTarget,
} from './forceClickGuideTourElement'

export type GuideTourScrollTargetOptions = {
  /** True when more than one unseen tour is queued on the current route. */
  isRouteTourChain: boolean
}

const hasGuideTourStepScrollTarget = (
  step: GuideTourStep | null | undefined
): boolean => Boolean(step?.targetId?.trim())

/**
 * Scroll is automatic whenever the step highlights a target.
 * `autoScrollToTarget: false` on the step or tour opts out (rare).
 * Route queues and multi-route onboard tours always scroll.
 */
export const shouldGuideTourForceScrollToTarget = (
  step: GuideTourStep | null | undefined,
  tour: GuideTourDefinition | null | undefined,
  { isRouteTourChain }: GuideTourScrollTargetOptions
): boolean => {
  if (isRouteTourChain) {
    return true
  }

  if (tour && isMultiRouteGuideTour(tour)) {
    return true
  }

  if (step?.autoScrollToTarget === false) {
    return false
  }

  if (tour?.autoScrollToTarget === false) {
    return false
  }

  return hasGuideTourStepScrollTarget(step)
}

/**
 * Optional scroll target when it differs from `targetId`
 * (e.g. scroll a section container, highlight a child button).
 */
export const getGuideTourStepScrollTargetId = (
  step: GuideTourStep | null | undefined,
  targetId: string | null | undefined
): string | null => {
  const configuredScrollTargetId = step?.scrollIntoView?.trim()

  if (configuredScrollTargetId) {
    return configuredScrollTargetId
  }

  return targetId ?? null
}

export const resolveGuideTourStepScrollTargetId = (
  step: GuideTourStep | null | undefined,
  targetId: string | null | undefined,
  tour: GuideTourDefinition | null | undefined,
  options: GuideTourScrollTargetOptions
): string | null => {
  if (!targetId || !shouldGuideTourForceScrollToTarget(step, tour, options)) {
    return null
  }

  return getGuideTourStepScrollTargetId(step, targetId)
}

export const isGuideTourStepTargetMissing = (
  targetId: string | null | undefined
): boolean => {
  const normalizedTargetId = targetId?.trim()

  if (!normalizedTargetId) {
    return false
  }

  return getGuideTourTargetAvailability(normalizedTargetId) === 'missing'
}

const isGuideTourStepReachableOnPathname = (
  tour: GuideTourDefinition,
  stepIndex: number,
  pathname: string
): boolean => {
  if (isGuideTourStepOnPathname(tour, stepIndex, pathname)) {
    const step = tour.steps[stepIndex]

    return (
      step?.reachableWhenTargetMissing === true ||
      !isGuideTourStepTargetMissing(step?.targetId)
    )
  }

  const step = tour.steps[stepIndex]
  const stepRoute = step ? getGuideTourStepRoute(tour, step) : undefined

  return Boolean(stepRoute)
}

export const findPreviousReachableGuideTourStepIndex = (
  tour: GuideTourDefinition,
  fromIndex: number,
  pathname: string,
  now: Date = new Date()
): number | null => {
  for (
    let index = Math.min(fromIndex, tour.steps.length - 1);
    index >= 0;
    index -= 1
  ) {
    const step = tour.steps[index]

    if (!step || isGuideTourStepExpired(step, now)) {
      continue
    }

    if (!isGuideTourStepReachableOnPathname(tour, index, pathname)) {
      continue
    }

    return index
  }

  return null
}

export const findNextReachableGuideTourStepIndex = (
  tour: GuideTourDefinition,
  fromIndex: number,
  pathname: string,
  now: Date = new Date()
): number | null => {
  for (let index = Math.max(0, fromIndex); index < tour.steps.length; index++) {
    const step = tour.steps[index]

    if (!step || isGuideTourStepExpired(step, now)) {
      continue
    }

    if (!isGuideTourStepReachableOnPathname(tour, index, pathname)) {
      continue
    }

    return index
  }

  return null
}

export const hasPreviousReachableGuideTourStep = (
  tour: GuideTourDefinition,
  stepIndex: number,
  pathname: string,
  now: Date = new Date()
): boolean =>
  findPreviousReachableGuideTourStepIndex(
    tour,
    stepIndex - 1,
    pathname,
    now
  ) !== null
